export const MYCIO_LANGUAGE_COOKIE = 'mycio_language'

export type MyCIOLanguageCode =
  | 'en'
  | 'es'
  | 'pt'
  | 'fr'
  | 'de'
  | 'it'
  | 'ru'
  | 'zh'
  | 'zh-Hant'
  | 'ja'
  | 'ko'
  | 'ar'
  | 'hi'
  | 'id'
  | 'tr'

export type MyCIOLanguage = {
  code: MyCIOLanguageCode
  label: string
  nativeLabel: string
  flagColor: string
}

export const MYCIO_LANGUAGES: readonly MyCIOLanguage[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', flagColor: '#21463A' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', flagColor: '#AA5334' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', flagColor: '#3E7A57' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', flagColor: '#335F8A' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch', flagColor: '#6B5A3A' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano', flagColor: '#5D7C63' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский', flagColor: '#7E4C54' },
  { code: 'zh', label: 'Chinese (Simplified)', nativeLabel: '简体中文', flagColor: '#A5443C' },
  { code: 'zh-Hant', label: 'Chinese (Traditional)', nativeLabel: '繁體中文', flagColor: '#A5443C' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語', flagColor: '#B85757' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어', flagColor: '#506B93' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', flagColor: '#547253' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', flagColor: '#B26B38' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia', flagColor: '#9B4F4A' },
  { code: 'tr', label: 'Turkish', nativeLabel: 'Türkçe', flagColor: '#8E463E' },
]

const supported = new Set<MyCIOLanguageCode>(MYCIO_LANGUAGES.map((language) => language.code))

const COUNTRY_LANGUAGE_DEFAULTS: Record<string, MyCIOLanguageCode> = {
  AR: 'es',
  AT: 'de',
  BE: 'fr',
  BR: 'pt',
  CA: 'en',
  CH: 'de',
  CL: 'es',
  CN: 'zh',       // Mainland → Simplified
  CO: 'es',
  DE: 'de',
  ES: 'es',
  FR: 'fr',
  GB: 'en',
  HK: 'zh-Hant', // Hong Kong → Traditional
  ID: 'id',
  IN: 'hi',
  IT: 'it',
  JP: 'ja',
  KR: 'ko',
  MX: 'es',
  PT: 'pt',
  RU: 'ru',
  SA: 'ar',
  SG: 'en',
  TR: 'tr',
  TW: 'zh-Hant', // Taiwan → Traditional
  US: 'en',
}

export function normalizeMyCIOLanguage(value: string | null | undefined): MyCIOLanguageCode | null {
  if (!value) return null
  const raw = value.trim().toLowerCase()
  // Model replies may name the language rather than return its code.
  const named = MYCIO_LANGUAGES.find((language) => language.label.toLowerCase() === raw || language.nativeLabel.toLowerCase() === raw)
  if (named) return named.code
  if (raw === 'chinese') return 'zh'
  // Traditional-Chinese variants (Taiwan / Hong Kong / Macau) resolve to the dedicated zh-Hant code;
  // bare 'zh', zh-Hans, zh-CN, zh-SG stay Simplified ('zh'). Must run BEFORE the region-stripping split.
  if (raw === 'zh-hant' || /^zh[-_](hant|tw|hk|mo)\b/.test(raw)) return 'zh-Hant'
  const code = raw.split(/[-_]/)[0] as MyCIOLanguageCode
  return supported.has(code) ? code : null
}

export function getMyCIOLanguage(code: string | null | undefined): MyCIOLanguage {
  return MYCIO_LANGUAGES.find((language) => language.code === normalizeMyCIOLanguage(code)) ?? MYCIO_LANGUAGES[0]
}

export function detectMyCIOLanguageFromCountry(countryCode: string | null | undefined): MyCIOLanguageCode | null {
  if (!countryCode) return null
  return COUNTRY_LANGUAGE_DEFAULTS[countryCode.trim().toUpperCase()] ?? null
}

export function detectMyCIOLanguageFromAcceptLanguage(header: string | null | undefined): MyCIOLanguageCode | null {
  if (!header) return null
  const weighted = header
    .split(',')
    .map((part) => {
      const [tag, qPart] = part.trim().split(';')
      const q = qPart?.startsWith('q=') ? Number(qPart.slice(2)) : 1
      return { code: normalizeMyCIOLanguage(tag), q: Number.isFinite(q) ? q : 0 }
    })
    .filter((part): part is { code: MyCIOLanguageCode; q: number } => Boolean(part.code))
    .sort((a, b) => b.q - a.q)

  return weighted[0]?.code ?? null
}

export function detectMyCIOLanguageFromNavigator(): MyCIOLanguageCode {
  if (typeof navigator === 'undefined') return 'en'
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of languages) {
    const normalized = normalizeMyCIOLanguage(language)
    if (normalized) return normalized
  }
  return 'en'
}
